import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorBoundaryContext, FallbackProps } from "react-error-boundary";

type Props = {
    children: ReactNode;
    fallbackRender: (props: FallbackProps) => ReactNode;
    onError?: (error: Error, info: ErrorInfo) => void;
};

type State = { error: Error | null };

// Calls the fallback render function from a function component of its own, so hooks inside it run
// there. Called straight from the class's render(), a fallback that reads context (ErrorFallback does)
// throws on React 19 (#321): React 18 still let a class render read context through hooks.
const FallbackRenderer = ({ render, ...props }: FallbackProps & { render: (props: FallbackProps) => ReactNode }) => <>{render(props)}</>;

/**
 * react-error-boundary's ErrorBoundary, minus its context provider while nothing has gone wrong.
 *
 * The library hands its context a new object on every render. Above content that React 18 is still
 * hydrating, any context change counts as an update to it, so the app's start-up renders made React
 * throw the server HTML away and render the page again in the browser (React #421). Here the context
 * only exists once an error is caught, for the fallbacks (they read resetErrorBoundary from it), and
 * its value only changes when the error does.
 */
class StableErrorBoundary extends Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        this.props.onError?.(error, info);
    }

    private reset = () => this.setState({ error: null });

    private contextValue: { didCatch: true; error: Error; resetErrorBoundary: () => void } | null = null;

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        if (this.contextValue?.error !== error) this.contextValue = { didCatch: true, error, resetErrorBoundary: this.reset };
        return (
            <ErrorBoundaryContext.Provider value={this.contextValue}>
                <FallbackRenderer render={this.props.fallbackRender} error={error} resetErrorBoundary={this.reset} />
            </ErrorBoundaryContext.Provider>
        );
    }
}

export default StableErrorBoundary;
